import React from "react";
import { createDataManagerColumnResizePreview } from "../utils/dataManagerColumnResizePreview.js";
import { resolveDataManagerFixedColumnWidth } from "../utils/dataManagerGridTableLayout.js";
import { onPreferencesLeave } from "../utils/preferencesDelegation.js";
import { subscribeUserUiPreferences } from "../lib/userUiPreferencesSync.js";

const COLUMN_RESIZE_MIN_WIDTH_PX = 48;
const COLUMN_PREFERENCES_SAVE_DEBOUNCE_MS = 600;
const COLUMN_WIDTH_MIN_PX = 1;
const COLUMN_WIDTH_MAX_PX = 4000;

export function buildResolvedColumns(baseColumns, columnOrder, columnWidths, savedWidths) {
  const baseColumnMap = new Map(baseColumns.map((col) => [col.key, col]));
  const seenKeys = new Set();
  const resolved = [];

  const order = Array.isArray(columnOrder) && columnOrder.length > 0
    ? columnOrder
    : baseColumns.map((col) => col.key);

  const resolvedSavedWidths = savedWidths && typeof savedWidths === "object" ? savedWidths : {};

  for (const key of order) {
    const baseCol = baseColumnMap.get(key);
    if (!baseCol || seenKeys.has(key)) {
      continue;
    }
    seenKeys.add(key);
    const currentWidth = typeof columnWidths?.[key] === "number" && columnWidths[key] > 0
      ? columnWidths[key]
      : typeof resolvedSavedWidths[key] === "number" && resolvedSavedWidths[key] > 0
        ? resolvedSavedWidths[key]
        : baseCol.defaultWidth;
    resolved.push({
      ...baseCol,
      defaultWidth: baseCol.defaultWidth ?? 100,
      minWidth: baseCol.minWidth ?? COLUMN_RESIZE_MIN_WIDTH_PX,
      width: currentWidth,
    });
  }

  for (const col of baseColumns) {
    if (!seenKeys.has(col.key)) {
      seenKeys.add(col.key);
      const currentWidth = typeof columnWidths?.[col.key] === "number" && columnWidths[col.key] > 0
        ? columnWidths[col.key]
        : col.defaultWidth ?? 100;
      resolved.push({
        ...col,
        defaultWidth: col.defaultWidth ?? 100,
        minWidth: col.minWidth ?? COLUMN_RESIZE_MIN_WIDTH_PX,
        width: currentWidth,
      });
    }
  }

  return resolved;
}

export function sanitizePersistedColumnWidth(width) {
  if (typeof width !== "number" || !Number.isFinite(width)) {
    return null;
  }
  return Math.min(COLUMN_WIDTH_MAX_PX, Math.max(COLUMN_WIDTH_MIN_PX, Math.round(width)));
}

export function sanitizePersistedColumnWidths(
  baseColumns,
  columnOrder,
  columnWidths,
  resizableFixedColumns = [],
) {
  const allowedKeys = new Set([
    ...normalizeColumnOrderKeys(baseColumns, columnOrder),
    ...resizableFixedColumns.map((column) => column?.key).filter(Boolean),
  ]);
  const sourceWidths = columnWidths && typeof columnWidths === "object" ? columnWidths : {};
  const sanitized = {};
  for (const [columnKey, rawWidth] of Object.entries(sourceWidths)) {
    if (!allowedKeys.has(columnKey)) {
      continue;
    }
    const width = sanitizePersistedColumnWidth(rawWidth);
    if (width != null) {
      sanitized[columnKey] = width;
    }
  }
  return sanitized;
}

function buildPersistedColumnWidths(baseColumns, columnOrder, columnWidths, savedWidths, resizableFixedColumns = []) {
  const widths = buildResolvedColumns(baseColumns, columnOrder, columnWidths, savedWidths).reduce((resolvedWidths, column) => {
    const width = sanitizePersistedColumnWidth(column.width);
    if (width != null) {
      resolvedWidths[column.key] = width;
    }
    return resolvedWidths;
  }, {});

  for (const column of resizableFixedColumns) {
    const width = sanitizePersistedColumnWidth(
      resolveDataManagerFixedColumnWidth(column, columnWidths, savedWidths),
    );
    if (width != null && column?.key) {
      widths[column.key] = width;
    }
  }

  return sanitizePersistedColumnWidths(baseColumns, columnOrder, widths, resizableFixedColumns);
}

export function buildColumnPreferencesSavePayload(
  baseColumns,
  { columnOrder, columnWidths, frozenColumnKeys } = {},
  resizableFixedColumns = [],
) {
  const orderedKeys = normalizeColumnOrderKeys(baseColumns, columnOrder);
  return {
    columnOrder: orderedKeys,
    columnWidths: sanitizePersistedColumnWidths(
      baseColumns,
      orderedKeys,
      columnWidths,
      resizableFixedColumns,
    ),
    frozenColumnKeys: Array.isArray(frozenColumnKeys) ? [...frozenColumnKeys] : [],
  };
}

function normalizeSavedColumnPreferences(preferences, fallback = {}) {
  const columnOrder = Array.isArray(preferences?.column_order)
    ? preferences.column_order
    : Array.isArray(fallback.columnOrder) ? fallback.columnOrder : [];
  const columnWidths = preferences?.column_widths && typeof preferences.column_widths === "object"
    ? preferences.column_widths
    : fallback.columnWidths && typeof fallback.columnWidths === "object" ? fallback.columnWidths : {};
  const frozenColumnKeys = Array.isArray(preferences?.frozen_column_keys)
    ? preferences.frozen_column_keys
    : Array.isArray(fallback.frozenColumnKeys) ? fallback.frozenColumnKeys : [];
  return {
    columnOrder: [...columnOrder],
    columnWidths: { ...columnWidths },
    frozenColumnKeys: [...frozenColumnKeys],
  };
}

export function normalizeColumnOrderKeys(baseColumns, columnOrder) {
  return buildResolvedColumns(baseColumns, columnOrder, {}, {}).map((column) => column.key);
}

export function resolveColumnMoveOrder(currentOrder, fromKey, toKey, placement = null) {
  if (!Array.isArray(currentOrder) || currentOrder.length === 0) {
    return [];
  }

  const fromIndex = currentOrder.indexOf(fromKey);
  const toIndex = currentOrder.indexOf(toKey);
  if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
    return currentOrder;
  }

  const normalizedPlacement = placement === "before" || placement === "after"
    ? placement
    : fromIndex < toIndex
      ? "before"
      : "after";
  const nextOrder = currentOrder.filter((key) => key !== fromKey);
  const targetIndex = nextOrder.indexOf(toKey);
  if (targetIndex === -1) {
    return currentOrder;
  }

  nextOrder.splice(normalizedPlacement === "after" ? targetIndex + 1 : targetIndex, 0, fromKey);
  return nextOrder;
}

export default function useDataManagerColumnPreferences({
  baseColumns = [],
  getPersistedColumnWidths = null,
  onSaveError = null,
  resizableFixedColumns = [],
  saveColumnPreferences: saveFn = null,
  savedPrefs = null,
  tableKey = "",
  normalizeIncoming = null,
}) {
  const savedOrder = Array.isArray(savedPrefs?.column_order) && savedPrefs.column_order.length > 0
    ? savedPrefs.column_order
    : null;
  const savedWidths = savedPrefs?.column_widths && typeof savedPrefs.column_widths === "object"
    ? savedPrefs.column_widths
    : {};
  const savedFrozenColumnKeys = Array.isArray(savedPrefs?.frozen_column_keys)
    ? savedPrefs.frozen_column_keys
    : [];

  const [localColumnOrder, setLocalColumnOrder] = React.useState(null);
  const [columnWidths, setColumnWidths] = React.useState({});
  const [confirmedFrozenColumnKeys, setConfirmedFrozenColumnKeys] = React.useState(savedFrozenColumnKeys);
  const [saveError, setSaveError] = React.useState(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const saveTimerRef = React.useRef(null);
  const latestOrderRef = React.useRef(null);
  const latestWidthsRef = React.useRef(null);
  const frozenColumnKeysRef = React.useRef(savedFrozenColumnKeys);
  const confirmedPreferencesRef = React.useRef(normalizeSavedColumnPreferences(savedPrefs));
  const inFlightSaveRef = React.useRef(null);
  const isMountedRef = React.useRef(true);
  const onSaveErrorRef = React.useRef(onSaveError);
  const pendingSaveRef = React.useRef(null);
  const saveFnRef = React.useRef(saveFn);
  const tableKeyRef = React.useRef(tableKey);
  const baseColumnsRef = React.useRef(baseColumns);
  const savedWidthsRef = React.useRef(savedWidths);
  const columnWidthsRef = React.useRef(columnWidths);
  const resolvedColumnsRef = React.useRef([]);
  const resolvedColumnOrderRef = React.useRef([]);
  const localColumnOrderRef = React.useRef(localColumnOrder);
  const savedOrderRef = React.useRef(savedOrder);
  const getPersistedColumnWidthsRef = React.useRef(getPersistedColumnWidths);
  const resizableFixedColumnsRef = React.useRef(resizableFixedColumns);
  const columnResizeCleanupRef = React.useRef(null);
  const hasLivePreferencesRef = React.useRef(false);

  onSaveErrorRef.current = onSaveError;

  React.useEffect(() => {
    resizableFixedColumnsRef.current = resizableFixedColumns;
  }, [resizableFixedColumns]);

  React.useEffect(() => {
    saveFnRef.current = saveFn;
  }, [saveFn]);

  React.useEffect(() => {
    tableKeyRef.current = tableKey;
  }, [tableKey]);

  React.useEffect(() => {
    baseColumnsRef.current = baseColumns;
  }, [baseColumns]);

  React.useEffect(() => {
    savedWidthsRef.current = savedWidths;
  }, [savedWidths]);

  React.useEffect(() => {
    if (hasLivePreferencesRef.current) return;
    if (saveTimerRef.current !== null || inFlightSaveRef.current || pendingSaveRef.current) {
      return;
    }
    const normalizedOrder = savedOrder
      ? normalizeColumnOrderKeys(baseColumnsRef.current, savedOrder)
      : [];
    confirmedPreferencesRef.current = {
      ...normalizeSavedColumnPreferences(savedPrefs),
      columnOrder: normalizedOrder,
      columnWidths: sanitizePersistedColumnWidths(
        baseColumnsRef.current,
        normalizedOrder,
        savedWidths,
        resizableFixedColumnsRef.current,
      ),
    };
    frozenColumnKeysRef.current = savedFrozenColumnKeys;
    setConfirmedFrozenColumnKeys(savedFrozenColumnKeys);
    setLocalColumnOrder(null);
  }, [savedFrozenColumnKeys.join("\u0000"), savedOrder?.join("\u0000") ?? "", JSON.stringify(savedWidths)]);

  React.useEffect(() => {
    columnWidthsRef.current = columnWidths;
  }, [columnWidths]);

  React.useEffect(() => {
    getPersistedColumnWidthsRef.current = getPersistedColumnWidths;
  }, [getPersistedColumnWidths]);

  const normalizedSavedOrder = React.useMemo(
    () => (savedOrder ? normalizeColumnOrderKeys(baseColumns, savedOrder) : null),
    [baseColumns, savedOrder],
  );

  const effectiveColumnOrder = localColumnOrder ?? normalizedSavedOrder;

  const resolvedColumns = React.useMemo(
    () => buildResolvedColumns(baseColumns, effectiveColumnOrder, columnWidths, savedWidths),
    [baseColumns, effectiveColumnOrder, columnWidths, savedWidths],
  );

  const resolvedColumnOrder = React.useMemo(
    () => resolvedColumns.map((col) => col.key),
    [resolvedColumns],
  );

  localColumnOrderRef.current = localColumnOrder;
  savedOrderRef.current = savedOrder;
  resolvedColumnsRef.current = resolvedColumns;
  resolvedColumnOrderRef.current = resolvedColumnOrder;

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      columnResizeCleanupRef.current?.({ unmounting: true });
    };
  }, []);

  const resolvedFixedColumnWidths = React.useMemo(() => {
    const widths = {};
    for (const column of resizableFixedColumns) {
      widths[column.key] = resolveDataManagerFixedColumnWidth(column, columnWidths, savedWidths);
    }
    return widths;
  }, [columnWidths, resizableFixedColumns, savedWidths]);

  const persistWidths = React.useCallback((widths) => (
    buildPersistedColumnWidths(
      baseColumnsRef.current,
      resolvedColumnOrderRef.current,
      widths,
      savedWidthsRef.current,
      resizableFixedColumnsRef.current,
    )
  ), []);

  const resolveResizableColumn = React.useCallback((columnKey) => (
    resolvedColumnsRef.current.find((entry) => entry.key === columnKey)
    ?? resizableFixedColumnsRef.current.find((entry) => entry.key === columnKey)
    ?? null
  ), []);

  const resolvedColumnWidths = React.useMemo(
    () => {
      const widths = {};
      resolvedColumns.forEach((col) => {
        widths[col.key] = col.width;
      });
      return widths;
    },
    [resolvedColumns],
  );

  const applyConfirmedPreferences = React.useCallback((preferences, { publish = true } = {}) => {
    const confirmedPreferences = normalizeSavedColumnPreferences(preferences);
    const normalizedOrder = normalizeColumnOrderKeys(
      baseColumnsRef.current,
      confirmedPreferences.columnOrder,
    );
    confirmedPreferences.columnOrder = normalizedOrder;
    confirmedPreferences.columnWidths = sanitizePersistedColumnWidths(
      baseColumnsRef.current,
      normalizedOrder,
      confirmedPreferences.columnWidths,
      resizableFixedColumnsRef.current,
    );
    confirmedPreferencesRef.current = confirmedPreferences;
    if (!publish || !isMountedRef.current) {
      return confirmedPreferences;
    }
    frozenColumnKeysRef.current = [...confirmedPreferences.frozenColumnKeys];
    setConfirmedFrozenColumnKeys([...confirmedPreferences.frozenColumnKeys]);
    setLocalColumnOrder([...confirmedPreferences.columnOrder]);
    setColumnWidths({ ...confirmedPreferences.columnWidths });
    columnWidthsRef.current = { ...confirmedPreferences.columnWidths };
    return confirmedPreferences;
  }, []);

  const flushPendingSave = React.useCallback(async () => {
    if (inFlightSaveRef.current) {
      await inFlightSaveRef.current.catch(() => undefined);
      return pendingSaveRef.current ? flushPendingSave() : null;
    }

    const payload = pendingSaveRef.current;
    const currentSaveFn = saveFnRef.current;
    const currentTableKey = tableKeyRef.current;
    if (!payload || !currentSaveFn || !currentTableKey) {
      if (isMountedRef.current) {
        setIsSaving(false);
      }
      return null;
    }

    pendingSaveRef.current = null;
    if (isMountedRef.current) {
      setIsSaving(true);
      setSaveError(null);
    }

    const savePromise = Promise.resolve().then(() => currentSaveFn(currentTableKey, payload, { keepalive: false }));
    inFlightSaveRef.current = savePromise;
    try {
      const response = await savePromise;
      applyConfirmedPreferences(response, { publish: !pendingSaveRef.current });
      return response;
    } catch (error) {
      const resolvedError = error instanceof Error
        ? error
        : new Error("Impossible de sauvegarder les préférences de colonnes.");
      if (!pendingSaveRef.current) {
        applyConfirmedPreferences({
          column_order: confirmedPreferencesRef.current.columnOrder,
          column_widths: confirmedPreferencesRef.current.columnWidths,
          frozen_column_keys: confirmedPreferencesRef.current.frozenColumnKeys,
        });
      }
      if (isMountedRef.current) {
        setSaveError(resolvedError);
        try {
          onSaveErrorRef.current?.(resolvedError, {
            attemptedPreferences: payload,
            tableKey: currentTableKey,
          });
        } catch {}
      }
      throw resolvedError;
    } finally {
      if (inFlightSaveRef.current === savePromise) {
        inFlightSaveRef.current = null;
      }
      if (pendingSaveRef.current) {
        void flushPendingSave().catch(() => {});
      } else if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  }, [applyConfirmedPreferences]);

  React.useEffect(() => subscribeUserUiPreferences(`columns:${tableKey}`, (preferences) => {
    if (!isMountedRef.current || saveTimerRef.current !== null || inFlightSaveRef.current || pendingSaveRef.current || columnResizeCleanupRef.current) return false;
    hasLivePreferencesRef.current = true;
    const incoming = normalizeIncoming ? normalizeIncoming(preferences) : preferences;
    const next = normalizeSavedColumnPreferences(incoming);
    if (JSON.stringify(next) !== JSON.stringify(confirmedPreferencesRef.current)) applyConfirmedPreferences(incoming);
    return true;
  }), [applyConfirmedPreferences, normalizeIncoming, tableKey]);

  React.useEffect(() => onPreferencesLeave(() => {
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = null;
    return flushPendingSave();
  }), [flushPendingSave]);

  const persistOrder = React.useCallback((order, widths) => {
    const payload = buildColumnPreferencesSavePayload(
      baseColumnsRef.current,
      {
        columnOrder: order,
        columnWidths: widths,
        frozenColumnKeys: frozenColumnKeysRef.current,
      },
      resizableFixedColumnsRef.current,
    );
    latestOrderRef.current = payload.columnOrder;
    latestWidthsRef.current = payload.columnWidths;
    pendingSaveRef.current = payload;
    setSaveError(null);
    setIsSaving(true);
    if (saveTimerRef.current !== null) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      void flushPendingSave().catch(() => {});
    }, COLUMN_PREFERENCES_SAVE_DEBOUNCE_MS);
  }, [flushPendingSave]);

  React.useEffect(() => {
    return () => {
      if (saveTimerRef.current !== null) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      const currentSaveFn = saveFnRef.current;
      const currentTableKey = tableKeyRef.current;
      const pendingPayload = pendingSaveRef.current;
      pendingSaveRef.current = null;
      if (currentSaveFn && currentTableKey && pendingPayload) {
        currentSaveFn(currentTableKey, pendingPayload, { keepalive: true }).catch(() => {});
      }
    };
  }, []);

  const handleResizeEnd = React.useCallback((columnKey, newWidth) => {
    const column = resolveResizableColumn(columnKey);
    const minWidth = Math.max(
      1,
      Math.round(Number(column?.minWidth) || COLUMN_RESIZE_MIN_WIDTH_PX),
    );

    setColumnWidths((prev) => {
      const next = {
        ...prev,
        [columnKey]: Math.max(minWidth, Math.round(Number(newWidth) || 0)),
      };
      columnWidthsRef.current = next;
      persistOrder(
        resolvedColumnOrderRef.current,
        persistWidths(next),
      );
      return next;
    });
  }, [persistOrder, persistWidths, resolveResizableColumn]);

  const handleResizePointerDown = React.useCallback((event, columnKey) => {
    if (!columnKey) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const column = resolveResizableColumn(columnKey);
    if (!column) {
      return;
    }

    const minWidth = Math.max(
      1,
      Math.round(Number(column.minWidth) || COLUMN_RESIZE_MIN_WIDTH_PX),
    );
    const startX = Number(event.clientX) || 0;
    const startWidth = Math.max(
      minWidth,
      Math.round(
        Number(column.width)
        || resolveDataManagerFixedColumnWidth(column, columnWidthsRef.current, savedWidthsRef.current)
        || Number(column.defaultWidth)
        || 100,
      ),
    );
    const resizeHeaderCell = event.currentTarget?.closest?.("th") ?? null;
    const resizeTable = event.currentTarget?.closest?.("table") ?? null;
    let animationFrameId = null;
    let pendingWidth = startWidth;
    columnResizeCleanupRef.current?.();
    const resizePreview = createDataManagerColumnResizePreview({
      headerCell: resizeHeaderCell,
      pointerX: startX,
      table: resizeTable,
    });

    const applyPendingWidth = () => {
      animationFrameId = null;
      resizePreview.update(pendingWidth - startWidth);
    };

    const commitPendingWidth = () => {
      const currentWidths = columnWidthsRef.current;
      const currentValue = typeof currentWidths[columnKey] === "number" && currentWidths[columnKey] > 0
        ? currentWidths[columnKey]
        : startWidth;
      if (currentValue === pendingWidth) {
        return;
      }

      const next = {
        ...currentWidths,
        [columnKey]: pendingWidth,
      };
      columnWidthsRef.current = next;
      setColumnWidths(next);
    };

    const handlePointerMove = (moveEvent) => {
      const deltaX = (Number(moveEvent.clientX) || 0) - startX;
      pendingWidth = Math.max(minWidth, Math.round(startWidth + deltaX));
      if (animationFrameId === null) {
        animationFrameId = window.requestAnimationFrame(applyPendingWidth);
      }
    };

    const suppressClick = (clickEvent) => {
      clickEvent.stopPropagation();
      clickEvent.preventDefault();
      document.removeEventListener("click", suppressClick, true);
    };

    const cleanup = (cleanupOptions = null) => {
      const isUnmounting = cleanupOptions?.unmounting === true;
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
      if (!isUnmounting) {
        commitPendingWidth();
      }
      resizePreview.remove();
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("pointerup", cleanup, true);
      document.removeEventListener("pointercancel", cleanup, true);
      if (!isUnmounting) {
        document.addEventListener("click", suppressClick, true);
      }
      if (columnResizeCleanupRef.current === cleanup) {
        columnResizeCleanupRef.current = null;
      }
      if (!isUnmounting) {
        persistOrder(
          resolvedColumnOrderRef.current,
          persistWidths(columnWidthsRef.current),
        );
      }
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", cleanup, true);
    document.addEventListener("pointercancel", cleanup, true);
    columnResizeCleanupRef.current = cleanup;
  }, [persistOrder, persistWidths, resolveResizableColumn]);

  const resolvePersistedColumnWidths = React.useCallback(() => {
    const externalWidths = typeof getPersistedColumnWidthsRef.current === "function"
      ? getPersistedColumnWidthsRef.current()
      : null;
    if (externalWidths && typeof externalWidths === "object") {
      return externalWidths;
    }
    return resolvedColumnWidths;
  }, [resolvedColumnWidths]);

  const handleReorderEnd = React.useCallback((newOrder) => {
    if (!Array.isArray(newOrder) || newOrder.length === 0) {
      return;
    }
    const orderedKeys = normalizeColumnOrderKeys(baseColumns, newOrder);
    setLocalColumnOrder(orderedKeys);
    persistOrder(orderedKeys, resolvePersistedColumnWidths());
  }, [baseColumns, persistOrder, resolvePersistedColumnWidths]);

  const handleFrozenColumnKeysChange = React.useCallback((nextFrozenColumnKeys) => {
    frozenColumnKeysRef.current = Array.isArray(nextFrozenColumnKeys)
      ? [...nextFrozenColumnKeys]
      : [];
    persistOrder(
      resolvedColumnOrderRef.current,
      resolvePersistedColumnWidths(),
    );
  }, [persistOrder, resolvePersistedColumnWidths]);

  const handleColumnMove = React.useCallback((fromKey, toKey, placement = null) => {
    const currentOrder = normalizeColumnOrderKeys(
      baseColumnsRef.current,
      localColumnOrderRef.current ?? savedOrderRef.current,
    );
    const newOrder = resolveColumnMoveOrder(currentOrder, fromKey, toKey, placement);
    if (newOrder.join("\u0000") === currentOrder.join("\u0000")) {
      return;
    }
    handleReorderEnd(newOrder);
  }, [handleReorderEnd]);

  return {
    columnWidths: resolvedColumnWidths,
    handleColumnMove,
    handleFrozenColumnKeysChange,
    handleReorderEnd,
    handleResizeEnd,
    handleResizePointerDown,
    isSaving,
    resolvedColumns,
    resolvedColumnOrder,
    resolvedFixedColumnWidths,
    saveError,
    savedFrozenColumnKeys: confirmedFrozenColumnKeys,
  };
}
